import React, { useEffect, useRef } from 'react';
import { Modal, Spinner } from '../../components/ui';
import { VISIBILITIES, fmtDate } from '../../utils/helpers';

const CAP = 400; // render cap; search to narrow

// Bulk import from the phone book, built for a family member on their phone:
//   1. tap the functions to invite to (chips)
//   2. tick people (search, "select all", already-added ones are greyed out)
//   3. one big "Add N guests" button; progress while saving.
// Desktop contact import (only reachable when the browser Contact Picker exists): wide dialog.
export default function ContactImportDesktop({ vm }) {
  const {
    invitableFunctions: functions, bulkLoading, bulkSaving, bulkProgress, deviceContacts, bulkVisible, bulkSelectable,
    bulkSelected, toggleBulk, bulkSearch, setBulkSearch, bulkAllVisibleSelected, toggleBulkAll,
    bulkFunctionIds, setBulkFunctionIds, bulkSide, bulkVisibility, setBulkVisibility,
    setBulkOpen, saveBulk, isMarriage,
  } = vm;

  const shown = bulkVisible.slice(0, CAP);
  const count = bulkSelected.size;
  const noFn = bulkFunctionIds.length === 0;
  const searchRef = useRef(null);
  useEffect(() => { if (!bulkLoading && searchRef.current) searchRef.current.focus(); }, [bulkLoading]);

  const toggleFn = (id) => vm.toggleBulkFunction(id); // Wedding rule lives in the hook
  const allFns = bulkFunctionIds.length === functions.length;

  return (
    <Modal
      dense
      title="Add guests from Contacts"
      onClose={() => !bulkSaving && setBulkOpen(false)}
      className="contact-import-modal gx-ci-modal"
      footer={<>
        <button className="btn light" onClick={() => setBulkOpen(false)} disabled={bulkSaving}>Cancel</button>
        <button className="btn primary" style={{ minWidth: 220 }} disabled={bulkSaving || count === 0 || noFn} onClick={saveBulk}>
          {bulkSaving ? `Adding ${bulkProgress.done} / ${bulkProgress.total}…`
            : noFn ? 'Pick a function first'
            : count === 0 ? 'Tick people to add'
            : `Add ${count} guest${count === 1 ? '' : 's'} to ${bulkFunctionIds.length} function${bulkFunctionIds.length === 1 ? '' : 's'}`}
        </button>
      </>}
    >
      <div className="contact-import-shell">
        {/* Step 1 — functions as tap chips */}
        <div className="gx-fn-head">
          <span className="step-label">1. Invite to which functions?</span>
          {functions.length > 1 && (
            <span className="gx-fn-tools">
              <button type="button" className="link-btn" onClick={() => setBulkFunctionIds(allFns ? [] : functions.map(f => String(f.FunctionId)))}>
                {allFns ? 'None' : 'All'}
              </button>
            </span>
          )}
        </div>
        <div className="gx-fn-grid">
          {functions.map(f => {
            const on = bulkFunctionIds.includes(String(f.FunctionId));
            return (
              <label key={f.FunctionId} className={'cg-fn' + (on ? ' on' : '')} title={f.FunctionName}>
                <input type="checkbox" checked={on} onChange={() => toggleFn(f.FunctionId)} />
                <span className="cg-fn-name">{f.FunctionName}<span className="fn-count">({vm.fnCount(f.FunctionId)})</span></span>
                <span className="cg-fn-date">{fmtDate(f.FunctionDate).slice(0, 5)}</span>
              </label>
            );
          })}
        </div>

        <div className="gx-ci-opts">
          {/* No side picker: guests take the adder's own side (My Profile). */}
          {isMarriage && bulkSide && <span className={`chip ${bulkSide === 'Groom' ? 'male' : 'female'}`}>{bulkSide} side</span>}
          <label className="inline-select">Visible to
            <select value={bulkVisibility} onChange={e => setBulkVisibility(e.target.value)}>
              {VISIBILITIES.map(v => <option key={v} value={v}>{v === 'Private' ? 'Only me + admin' : 'Everyone'}</option>)}
            </select>
          </label>
        </div>

        {/* Step 2 — people */}
        <div className="step-label" style={{ marginTop: 10 }}>2. Tick the people to invite</div>
        {bulkLoading ? <Spinner label="Reading your contacts…" /> : (
          <>
            <div className="contact-import-search">
              <input ref={searchRef} placeholder="Search name or number…" value={bulkSearch} onChange={e => setBulkSearch(e.target.value)} />
              {bulkSearch && <button className="btn light sm" onClick={() => setBulkSearch('')}>✕</button>}
              <button className="btn light sm" onClick={toggleBulkAll} disabled={!bulkSelectable.length}>
                {bulkAllVisibleSelected ? 'Clear' : `All ${bulkSelectable.length}`}
              </button>
            </div>
            <div className="muted contact-import-count">
              <strong>{count} selected</strong> · {deviceContacts.length} contacts
              {bulkVisible.length > CAP ? ` · showing first ${CAP}, search to narrow` : ''}
            </div>

            <div className="contact-list">
              {shown.length === 0 ? (
                <div className="empty contact-empty">No contacts match "{bulkSearch}".</div>
              ) : shown.map(c => {
                const on = bulkSelected.has(c.key);
                return (
                  <label key={c.key} className={'contact-row' + (on ? ' on' : '') + (c.added ? ' added' : '')}>
                    <input type="checkbox" checked={on} disabled={c.added} onChange={() => toggleBulk(c.key)} />
                    <span className="avatar sm">{(c.display || '?').trim().charAt(0).toUpperCase()}</span>
                    <span className="contact-row-main">
                      <div className="contact-name" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.display}</div>
                      <div className="muted contact-mobile">{c.mobile || 'no mobile number'}</div>
                    </span>
                    {c.added && <span className="chip ok">Added</span>}
                  </label>
                );
              })}
            </div>
            <div className="muted contact-import-note">
              Greyed = already in your list for the chosen functions. Guests you add are private to you unless you choose "Everyone".
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
